import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { PartnerService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'

export interface AdminOrganizationQueryFilters {
  type?: PartnerService.OrganizationType
  active?: boolean
}

// Type-safe adapter to match generated API parameter type
const toQueryFiltersParam = (filters: AdminOrganizationQueryFilters): PartnerService.OrganizationsGetQueryFiltersParameter => filters

export interface UseAdminOrganizationsParams {
  queryFilters: AdminOrganizationQueryFilters
  sortField?: keyof PartnerService.Organization
  sortDirection?: 'asc' | 'desc'
  initialPageSize?: number
}

const DEFAULT_PAGE_SIZE = 30

export const adminOrganizationsQueryKeys = {
  all: () => ['admin-organizations'] as const,
  list: (params: { queryFilters: AdminOrganizationQueryFilters; sortField?: keyof PartnerService.Organization; sortDirection?: 'asc' | 'desc' }) =>
    ['admin-organizations', params] as const,
}

const fetchAdminOrganizationsPage = async ({
  pageParam,
  queryFilters,
  sortField,
  sortDirection,
}: {
  pageParam: { limit: number; offset: number }
  queryFilters: AdminOrganizationQueryFilters
  sortField?: keyof PartnerService.Organization
  sortDirection?: 'asc' | 'desc'
}): Promise<PartnerService.Organization[]> => {
  const { limit, offset } = pageParam
  const response = await apis.partnerService.organizationsGet(
    toQueryFiltersParam(queryFilters),
    sortField as PartnerService.OrganizationSortFieldEnum | undefined,
    sortDirection as PartnerService.SortDirectionEnum | undefined,
    limit,
    offset
  )
  return response.data.data
}

export const useAdminOrganizations = ({
  queryFilters,
  sortField,
  sortDirection,
  initialPageSize = DEFAULT_PAGE_SIZE,
}: UseAdminOrganizationsParams) => {
  const query = useInfiniteQuery({
    queryKey: adminOrganizationsQueryKeys.list({ queryFilters, sortField, sortDirection }),
    queryFn: ({ pageParam }) =>
      fetchAdminOrganizationsPage({
        pageParam: pageParam ?? { limit: initialPageSize, offset: 0 },
        queryFilters,
        sortField,
        sortDirection,
      }),
    initialPageParam: { limit: initialPageSize, offset: 0 },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length === 0) return undefined
      const currentOffset = allPages.reduce((sum, page) => sum + page.length, 0)
      const nextLimit = allPages.length === 1
        ? initialPageSize * 2
        : Math.min(initialPageSize * 2 ** allPages.length, initialPageSize * 2 ** 8)
      return { limit: nextLimit, offset: currentOffset }
    },
  })

  const organizations = useMemo(() => query.data?.pages.flat() ?? [], [query.data])
  const hasMore = !!query.hasNextPage

  return {
    organizations,
    hasMore,
    isInitialLoading: query.isPending,
    isFetchingNextPage: query.isFetchingNextPage,
    isError: query.isError,
    error: query.error,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  }
}
